import React from 'react';
import { Typography, styled } from '@mui/material';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { Button } from 'components/Button/Button';
import { useThemeUtils } from 'contexts/ThemeContext/hooks/useThemeUtils';
import { AssessmentsPageCircularIndicator } from './AssessmentsPageCircularIndicator';

interface AssessmentsPageIndicatorProps {
  /**
   * Page number to display
   */
  pageNumber: number;
  /**
   * Total number of questions on the page
   */
  questionCount: number;
  /**
   * Number of questions completed on the page
   */
  questionsCompleted: number;
  /**
   * Callback when the indicator is clicked
   */
  onClick: (page: number) => void;
  /**
   * Whether this page indicator is active/selected
   */
  isActive?: boolean;
}

const DesktopButton = styled(Button, {
  shouldForwardProp: (props) => props !== 'ispageNotStarted' && props !== 'isPageCompleted',
})<{ ispageNotStarted: boolean; isPageCompleted: boolean }>(
  ({ theme, ispageNotStarted, isPageCompleted }) => ({
    minWidth: theme.spacing(5.5),
    padding: 0,
    backgroundColor: ispageNotStarted
      ? theme.palette.brandBackgroundPrimary.main
      : theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.grey[400],
      opacity: isPageCompleted ? 0.8 : 1,
    },
  }),
);

const MobileButton = styled(Button, {
  shouldForwardProp: (props) => props !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  width: '100%',
  justifyContent: 'space-between',
  backgroundColor: isActive
    ? theme.palette.common.white
    : theme.palette.brandBackgroundPrimary.main,
  border: `3px solid ${isActive ? theme.palette.primary.main : 'transparent'}`,
  padding: theme.spacing(1, 1, 1, 4),
  '&:hover': {
    backgroundColor: theme.palette.grey[400],
  },
}));

export const AssessmentsPageIndicator = ({
  pageNumber,
  questionCount,
  questionsCompleted,
  isActive = false,
  onClick,
}: AssessmentsPageIndicatorProps) => {
  const { isMobile } = useDeviceType();
  const { theme } = useThemeUtils();

  const handleOnClick = () => {
    onClick(pageNumber);
  };

  const isPageCompleted = questionsCompleted === questionCount;

  const ispageNotStarted = questionsCompleted === 0 && questionCount > 0;

  const ariaLabel = isActive
    ? `Current page ${pageNumber}, ${questionsCompleted} of ${questionCount} questions completed`
    : `Go to page ${pageNumber}, ${questionsCompleted} of ${questionCount} questions completed`;

  if (isMobile) {
    return (
      <MobileButton
        id={`assessment__page-indicator-button-${pageNumber}`}
        isActive={isActive}
        onClick={handleOnClick}
        aria-label={ariaLabel}
      >
        <Typography
          variant="label"
          sx={{
            color: isActive ? theme.palette.grey[600] : theme.palette.brandShade2.main,
          }}
        >
          Page
        </Typography>
        <AssessmentsPageCircularIndicator
          pageNumber={pageNumber}
          questionCount={questionCount}
          questionsCompleted={questionsCompleted}
          isActive={isActive}
        />
      </MobileButton>
    );
  }

  return (
    <DesktopButton
      id={`assessment__page-indicator-button-${pageNumber}`}
      ispageNotStarted={ispageNotStarted}
      onClick={handleOnClick}
      aria-label={ariaLabel}
      isPageCompleted={isPageCompleted}
    >
      <AssessmentsPageCircularIndicator
        pageNumber={pageNumber}
        questionCount={questionCount}
        questionsCompleted={questionsCompleted}
        isActive={isActive}
      />
    </DesktopButton>
  );
};
